import './instrumentation';
import styled from '@emotion/styled';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import BackgroundImage from './assets/images/verantos-background.png';
import { type Messages } from './common/types';
import { ErrorFallback } from './components/ErrorFallback';
import { AuthProvider } from './core/auth';
import { SplitFFProvider } from './core/utils';
import { store } from './lib/store';
import locale_en from './locale/en';
import { AppThemeProvider, GlobalStyles } from './styles';

import './assets/fonts/roboto.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'dayjs/locale/en';

// Mui X Pro License
LicenseInfo.setLicenseKey(
  '7ab0abc518f1ec6ce43e50d7d9280a94Tz0xMjA1NTgsRT0xNzkyMjgxNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=' // pragma: allowlist secret
);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
const language = navigator.language.split(/[-_]/)[0]; // language without region code

const messages: Messages = {
  en: locale_en,
};

const AppBackground = styled.div`
  background-image: url(${BackgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
  height: 100%;
  width: 100%;
`;
root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <GlobalStyles />
      <AppBackground>
        <Provider store={store}>
          <IntlProvider locale={language} messages={messages[language]} defaultLocale="en">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <AuthProvider>
                <SplitFFProvider>
                  <AppThemeProvider>
                    <BrowserRouter basename={import.meta.env.BASE_URL}>
                      <App />
                    </BrowserRouter>
                  </AppThemeProvider>
                </SplitFFProvider>
              </AuthProvider>
            </LocalizationProvider>
          </IntlProvider>
        </Provider>
      </AppBackground>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
